import { motion, useReducedMotion } from 'framer-motion';
import { Linkedin } from 'lucide-react';
import { Link, type To } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import {
  CandidateCard,
  CandidateCardBackground,
  CandidateCardContent,
  CandidateCardHeader,
} from '@/modules/candidates/components/CandidateCard';
import {
  mapPublicWorkExperiencesForCard,
  type ShortlistCandidate,
} from '@/modules/public-shortlist/mapPublicCandidate';
import {
  getShortlistRevealInitial,
  SHORTLIST_EASE,
  SHORTLIST_LOAD_DELAYS,
  shortlistRevealVisible,
} from '@/modules/public-shortlist/shortlistMotion';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Separator } from '@/modules/ui/components/separator/Separator';

interface ShortlistCandidateCardProps {
  candidate: ShortlistCandidate;
  detailHref?: To;
  index?: number;
  preview?: boolean;
  onNavigateToDetail?: () => void;
}

export function ShortlistCandidateCard({
  candidate,
  detailHref,
  index = 0,
  preview = false,
  onNavigateToDetail,
}: ShortlistCandidateCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const workExperiences = mapPublicWorkExperiencesForCard(candidate);
  const hasStrengths = candidate.strengths.length > 0;
  const hasContent =
    workExperiences.length > 0 || hasStrengths || Boolean(candidate.summary);

  return (
    <motion.div
      initial={getShortlistRevealInitial(prefersReducedMotion)}
      animate={shortlistRevealVisible}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : {
              duration: 0.4,
              delay:
                SHORTLIST_LOAD_DELAYS.cardsBase + Math.min(index * 0.05, 0.2),
              ease: SHORTLIST_EASE,
            }
      }
      aria-hidden={preview}
      className={cn(preview && 'pointer-events-none opacity-70')}
    >
      <CandidateCard
        animate={false}
        className={cn(
          preview && 'border-dashed border-neutral-300/80 bg-neutral-100/60',
        )}
      >
        <CandidateCardHeader className="md:px-8 md:py-5">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="inline-flex min-w-0 items-center gap-2">
              {detailHref && !preview ? (
                <Link
                  to={detailHref}
                  onClick={onNavigateToDetail}
                  className="text-base leading-none font-semibold text-neutral-900 hover:underline md:text-lg"
                >
                  {candidate.fullName}
                </Link>
              ) : (
                <span className="text-base leading-none font-semibold text-neutral-900 md:text-lg">
                  {candidate.fullName}
                </span>
              )}

              {!preview && candidate.linkedinUrl ? (
                <a
                  href={candidate.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${candidate.fullName}'s LinkedIn profile`}
                  className="inline-flex shrink-0 items-center justify-center text-[#0A66C2] transition-colors hover:text-[#004182]"
                >
                  <Linkedin className="size-4" aria-hidden />
                </a>
              ) : null}
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <Paragraph variant="caption" className="text-neutral-600">
                {candidate.title}
                {candidate.company ? (
                  <>
                    {' '}
                    <span className="text-neutral-500">at</span>{' '}
                    {candidate.company}
                  </>
                ) : null}
              </Paragraph>

              {candidate.location ? (
                <>
                  <Paragraph variant="caption" className="shrink-0">
                    |
                  </Paragraph>
                  <Paragraph variant="caption" className="text-neutral-600">
                    {candidate.location}
                  </Paragraph>
                </>
              ) : null}
            </div>
          </div>
        </CandidateCardHeader>

        {hasContent ? (
          <>
            <Separator />
            <CandidateCardContent className="md:px-8 md:py-6">
              {candidate.summary && (
                <p className="mb-5 text-sm leading-6 text-neutral-700">
                  {candidate.summary}
                </p>
              )}
              <CandidateCardBackground
                workExperiences={workExperiences}
                strengths={candidate.strengths}
                defaultExpanded
              />
            </CandidateCardContent>
          </>
        ) : null}
      </CandidateCard>
    </motion.div>
  );
}
